import React, { Component } from 'react';
/**
 * ExplanationCard — Teacher note aesthetic for explanations.
 *
 * Correct: warm amber "Waarom dit klopt" card.
 * Wrong: rose "Waarom was dat niet goed?" + amber teacher note.
 * Compact mode for inline usage.
 *
 * Co-located in practice/ domain (CL-1 s64).
 */
import { motion } from 'framer-motion';
import { Lightbulb, CheckCircle2, Info, Zap, BookOpen } from 'lucide-react';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { Card } from '../../../components/ui/Card';
import { ExplanationSteps } from './ExplanationSteps';
// ── Types ────────────────────────────────────────────────────
interface ExplanationCardProps {
  explanation: string;
  distractorExplanation?: string;
  isCorrect: boolean;
  mode?: 'full' | 'compact';
  solutionSteps?: string[];
}
// ── Component ────────────────────────────────────────────────
export function ExplanationCard({
  explanation,
  distractorExplanation,
  isCorrect,
  mode = 'full',
  solutionSteps
}: ExplanationCardProps) {
  if (mode === 'compact') {
    return <div className="bg-amber-50 p-4 rounded-xl border border-amber-200" data-id="element-10710" data-mp="26s3l9lgdwo8d">
        <div className="flex items-center gap-2 font-bold text-amber-800 mb-2 text-sm" data-id="element-10711" data-mp="2brg7c3zflr9w">
          <Zap className="w-4 h-4" data-id="element-10712" data-mp="2gl28p005a8qe" /> Uitleg
        </div>
        <div className="text-amber-900 text-sm leading-relaxed" data-id="element-10713" data-mp="2likkcy5jr223">
          <LatexRenderer content={explanation} data-id="element-10714" data-mp="2lkwzz4nu10bz" />
        </div>
      </div>;
  }
  return <div className="space-y-5" data-id="element-10715" data-mp="1nwwme8tg0bog">
      {/* Wrong answer — why was it wrong? */}
      {!isCorrect && distractorExplanation && <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-10716" data-mp="1xt9h9a0q4vks">
          <Card className="border-2 border-rose-100 bg-white overflow-hidden shadow-sm rounded-[2rem]" data-id="element-10717" data-mp="39arb9hsi6gu4">
            <div className="bg-rose-50 px-6 py-4 border-b border-rose-100 flex items-center gap-3" data-id="element-10718" data-mp="3dbup5yqzq2sp">
              <div className="bg-white p-2 rounded-xl shadow-sm border border-rose-100 text-rose-500" data-id="element-10719" data-mp="1965mtqz0qm2v">
                <Info className="w-5 h-5" data-id="element-10720" data-mp="1n5y6x3g1i41v" />
              </div>
              <h3 className="text-lg font-black text-rose-900" data-id="element-10721" data-mp="1n55b2ximcyyi">
                Waarom was dat niet goed?
              </h3>
            </div>
            <div className="p-7 bg-rose-50/20" data-id="element-10722" data-mp="ugzp67ftx3x3">
              <ExplanationSteps text={distractorExplanation} accent="indigo" data-id="element-10723" data-mp="uhsmkhsweh2k" />
            </div>
          </Card>
        </motion.div>}

      {/* Teacher note — warm amber/yellow */}
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: !isCorrect && distractorExplanation ? 0.1 : 0,
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }} data-id="element-10724" data-mp="147uczg8q4bkn">
        <div className="relative rounded-[2rem] overflow-hidden border-2 border-amber-200 shadow-md" data-id="element-10725" data-mp="6reuirabci7z">
          <div className="h-1.5 w-full bg-amber-400" data-id="element-10726" data-mp="2idqo7b3oodih" />
          <div className="bg-amber-50 px-6 py-4 border-b border-amber-100 flex items-center gap-3" data-id="element-10727" data-mp="2n60bejdeyzh2">
            <div className="bg-white p-2 rounded-xl shadow-sm border border-amber-200" data-id="element-10728" data-mp="29f52j72x0idh">
              {isCorrect ? <CheckCircle2 className="w-5 h-5 text-emerald-500" data-id="element-10729" data-mp="2ee87ysmt4qlz" /> : <Lightbulb className="w-5 h-5 text-amber-500 fill-amber-100" data-id="element-10730" data-mp="3242jn6ve494n" />}
            </div>
            <div className="flex-1" data-id="element-10731" data-mp="363b8pyfhjitv">
              <h3 className="text-lg font-black text-amber-900" data-id="element-10732" data-mp="3629whqmqgqak">
                {isCorrect ? 'Waarom dit klopt ✓' : 'Zo los je dit op'}
              </h3>
              <p className="text-xs text-amber-600 font-medium mt-0.5 flex items-center gap-1" data-id="element-10733" data-mp="2x2mvh5xx1mi1">
                <BookOpen className="w-3 h-3" data-id="element-10734" data-mp="1gowrdz6sqy8e" /> Uitleg van de juf/meester
              </p>
            </div>
          </div>
          <div className="bg-amber-50/60 p-7" data-id="element-10735" data-mp="1lq2mg3nxixl3">
            <div className="relative" data-id="element-10736" data-mp="20yxzlf1jraxp">
              <ExplanationSteps text={explanation || 'Goed gedaan! Je hebt de juiste strategie gebruikt.'} steps={solutionSteps} accent="amber" data-id="element-10737" data-mp="20zz7xbr6hjbq" />
            </div>
          </div>
        </div>
      </motion.div>
    </div>;
}